import React, { useEffect, useState } from 'react';
import { Button, Menu, MenuItem, Wrapper, closeMenu } from 'react-aria-menubutton';
import _ from 'underscore';
import classnames from 'classnames';
import { Portal } from 'react-portal';
import { useResponsive } from 'helpers/use_responsive';
import Icon from './icon';

interface Option {
  value?: string | number;
  id?: number;
  name?: string;
  label?: string;
  sectionHeader?: boolean;
  options?: Option[];
  iconClass?: string;
}

interface DropdownProps {
  children?: any | any[];
  options: Option[];
  onSelect: (value: any) => void;
  hideEmptyOption?: boolean;
  minWidth?: number;
  value?: string | number;
  placeholder?: string;
  emptyOptionLabel?: string;
  buttonType?: 'icon-more';
  buttonVariant?: 'outline-primary' | 'outline-primary-rounded' | 'clear' | 'primary' | 'link';
  position?: 'top';
  fixed?: boolean;
  size?: 'sm' | 'lg';
  disabled?: boolean;
  className?: string;
  emptyLabel?: boolean;
  mobileFullWidth?: boolean;
  header?: string;
  align?: 'right';
  menuClassName?: string;
}

interface Coords {
  top?: number;
  bottom?: number;
  left?: number | string | undefined;
  right?: number | undefined;
  transform?: string;
}

const Dropdown = ({
  children,
  options,
  onSelect,
  hideEmptyOption = false,
  minWidth,
  value,
  placeholder,
  emptyOptionLabel,
  buttonType,
  buttonVariant,
  position,
  fixed,
  size,
  disabled,
  className,
  emptyLabel,
  mobileFullWidth,
  header,
  align,
  menuClassName,
}: DropdownProps) => {
  const [ref, setRef] = useState<any>(null);
  const [coords, setCoords] = useState<Coords>({ right: 0, top: 0 });
  const [selectedSectionOptions, setSectionOptions] = useState<Option[]>([]);
  const { isMobile } = useResponsive();

  const [key, setKey] = useState(Math.random().toString());
  useEffect(() => {
    setKey(Math.random().toString());
  }, [onSelect]);
  const [open, setOpen] = useState(false);

  const wrapperClass = classnames({
    'btn-group': true,
    open,
    'dropdown--top': position === 'top',
    [className || '']: !!className,
  });

  const onMenuToggle = ({ isOpen }: { isOpen: boolean }) => {
    if (!ref) return;
    const { right, left, width, bottom, y } = ref.getBoundingClientRect();
    let c: Coords;

    if (mobileFullWidth && isMobile) {
      c = { top: 0, left: 0, right: 0, bottom: 0 };
    } else if (fixed) {
      c = {
        right: window.innerWidth - right,
        bottom: window.innerHeight - y + 5,
      };
    } else {
      const alignRight = align === 'right';
      const alignCenter = !alignRight && (isMobile || left + width > window.innerWidth);
      const leftCoords = !alignRight && isMobile ? (alignCenter ? '50%' : left) : undefined;
      const horizontalCoords: Pick<Coords, 'left' | 'right' | 'transform'> = alignRight
        ? { left: right + window.scrollX, transform: 'translateX(-100%)' }
        : {
            right: leftCoords ? undefined : window.innerWidth - right,
            left: leftCoords,
            transform: leftCoords && alignCenter ? 'translateX(-50%)' : undefined,
          };
      c =
        position === 'top'
          ? { bottom: window.innerHeight - window.scrollY - y + 5, ...horizontalCoords }
          : { top: bottom + window.scrollY, ...horizontalCoords };
    }

    setCoords(c);
    setOpen(isOpen);
    setSectionOptions([]);
  };

  const getOptionLabel = (option: Option) => option.label || option.name;

  const getOptions = () => {
    const items = selectedSectionOptions.length ? selectedSectionOptions : options;
    return items.map((opt) => ({
      ...opt,
      value: opt.value === undefined ? opt.id : opt.value,
    }));
  };

  const getLabel = () => {
    if (emptyLabel) return null;
    if (children) return children;
    const opts = getOptions();
    const selected = _.find(opts, (o) => o.value === value);
    if (selected) return getOptionLabel(selected);
    if (hideEmptyOption && opts.length) return getOptionLabel(opts[0]);
    return placeholder || 'Select...';
  };

  const _onSelect = (val: any) => {
    if (val.type === 'a') {
      onSelect(undefined);
      return;
    }
    onSelect(val);
  };

  if (!options.length) return null;

  return (
    <Wrapper
      onSelection={_onSelect}
      key={key}
      id={key}
      onMenuToggle={onMenuToggle}
      className={wrapperClass}
    >
      {buttonType === 'icon-more' ? (
        <Button
          ref={setRef as any}
          tag="button"
          className="dropdown-toggle transparent-button-icon transparent-button-icon--rotate-90"
          disabled={disabled}
        >
          <i className="icon-dots-3"></i>
        </Button>
      ) : (
        <Button
          ref={setRef as any}
          tag="button"
          className={classnames('btn dropdown-toggle', {
            'btn-hollow': !buttonVariant,
            [`btn-${buttonVariant}`]: !!buttonVariant,
            [`btn-${size}`]: !!size,
          })}
          disabled={disabled}
        >
          {getLabel()} {buttonVariant !== 'clear' && <i className="icon-chevron-down" />}
        </Button>
      )}

      <Portal>
        <Menu>
          <ul
            className={classnames(
              'dropdown-menu dropdown-menu--v2 dropdown-menu--in-portal is-right',
              {
                'dropdown-menu--top': position === 'top',
                [menuClassName || '']: !!menuClassName,
              },
            )}
            style={{
              ...coords,
              position: fixed || (mobileFullWidth && isMobile) ? 'fixed' : 'absolute',
            }}
          >
            {header && (
              <li>
                <MenuItem value={undefined} style={{ minWidth }}>
                  <a
                    className="dropdown-menu__header"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    {header}
                  </a>
                </MenuItem>
              </li>
            )}

            {!hideEmptyOption && emptyOptionLabel && (
              <li className={classnames({ active: !value })}>
                <MenuItem value={undefined} style={{ minWidth }}>
                  <a>{emptyOptionLabel}</a>
                </MenuItem>
              </li>
            )}

            {mobileFullWidth && isMobile && (
              <li>
                <a
                  className="dropdown-menu__close"
                  onClick={(e) => {
                    closeMenu(key);
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <i className="icon-cross align-right"></i>
                </a>
              </li>
            )}

            {selectedSectionOptions.length > 0 && (
              <li>
                <a
                  className="dropdown-menu__section-header"
                  onClick={(e) => {
                    setSectionOptions([]);
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <i className="icon-chevron-left margin-right-4" style={{ fontSize: '14px' }} />{' '}
                  Back
                </a>
              </li>
            )}

            {getOptions().map((option, idx) =>
              option.sectionHeader ? (
                option.options && option.options.length ? (
                  <li key={idx}>
                    <a
                      className="dropdown-menu__section-header"
                      onClick={(e) => {
                        setSectionOptions(option.options!);
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      {getOptionLabel(option)}{' '}
                      <i
                        className="icon-chevron-right margin-left-4"
                        style={{ fontSize: '14px' }}
                      />
                    </a>
                  </li>
                ) : null
              ) : (
                <li key={idx} className={classnames({ active: option.value === value })}>
                  <MenuItem value={option.value} style={{ minWidth }}>
                    <a className="align-center">
                      {option.iconClass && (
                        <Icon
                          name={option.iconClass as any}
                          width={20}
                          height={20}
                          className="margin-right-8"
                        />
                      )}
                      {getOptionLabel(option)}
                    </a>
                  </MenuItem>
                </li>
              ),
            )}
          </ul>
        </Menu>
      </Portal>
    </Wrapper>
  );
};

export default Dropdown;
